/* eslint-disable indent */
/* eslint-disable no-underscore-dangle */
import React, { useState, useCallback } from 'react'
import { ChatBubble } from './_components/ChatBubble'
import { ChatPanel } from './_components/ChatPanel'
import { sendChatMessage, submitChatRating } from './api/index'
import { ChatProps, ChatMessage } from './types'
import { DEFAULT_TEXTS } from './defaultTexts'
import { Sentry } from '../Sentry'
import './Chat.css'

type UserDataConsent = 'pending' | 'accepted' | 'declined'

const replaceText = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value),
    template
  )

export const Chat: React.FC<ChatProps> = ({
  establishmentUuid,
  apiEndpoints,
  logo,
  userData,
  onClearUserData,
  themeColors,
  texts: textsProp,
  language,
  bubblePosition = 'right',
}) => {
  const texts = textsProp || DEFAULT_TEXTS
  const { greeting } = texts.messages
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [conversationEnded, setConversationEnded] = useState(false)
  const [ratingSubmitted, setRatingSubmitted] = useState(false)
  const [userDataConsent, setUserDataConsent] =
    useState<UserDataConsent>('pending')

  const generateId = () =>
    `msg-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev && conversationEnded) {
        setMessages([])
        setSessionId(null)
        setConversationEnded(false)
        setRatingSubmitted(false)
        setUserDataConsent('pending')
      }
      if (!prev && messages.length === 0) {
        setMessages([{ id: generateId(), type: 'bot', text: greeting }])
      }
      return !prev
    })
  }, [greeting, messages.length, conversationEnded])

  const handleNotMe = useCallback(() => {
    setUserDataConsent('declined')
    if (onClearUserData) {
      onClearUserData()
    }
  }, [onClearUserData])

  const handleSend = useCallback(
    async (text: string) => {
      const isFirstUserMessage =
        messages.filter((m) => m.type === 'user').length === 0
      const shouldAttachUserData =
        isFirstUserMessage &&
        userDataConsent === 'pending' &&
        userData &&
        userData.firstName

      if (shouldAttachUserData) {
        setUserDataConsent('accepted')
      }

      // Build the message with optional context for the first message
      let messageToSend = text
      if (isFirstUserMessage) {
        let context = ''
        if (shouldAttachUserData) {
          const parts = [
            userData.firstName && `firstName: ${userData.firstName}`,
            userData.lastName && `lastName: ${userData.lastName}`,
            userData.email && `email: ${userData.email}`,
            userData.phone && `phone: ${userData.phone}`,
          ].filter(Boolean)
          context += `[Customer info: ${parts.join(', ')}]\n`
        }
        if (language) {
          context += `[Language: ${language}]\n`
        }
        if (context) {
          messageToSend = `${context}\n${text}`
        }
      }

      const userMessage: ChatMessage = { id: generateId(), type: 'user', text }
      setMessages((prev) => [...prev, userMessage])
      setIsLoading(true)

      try {
        const response = await sendChatMessage(apiEndpoints.message, {
          establishment_uuid: establishmentUuid,
          message: messageToSend,
          ...(sessionId && { session_id: sessionId }),
        })

        setSessionId(response._resource.session_id)
        if (response._resource.conversation_ended) {
          setConversationEnded(true)
        }
        const botMessage: ChatMessage = {
          id: generateId(),
          type: 'bot',
          text: response._resource.response,
        }
        setMessages((prev) => [...prev, botMessage])
      } catch (error) {
        Sentry.captureException(error)
        const errorMessage: ChatMessage = {
          id: generateId(),
          type: 'error',
          text: texts.error.generic,
        }
        setMessages((prev) => [...prev, errorMessage])
      } finally {
        setIsLoading(false)
      }
    },
    [
      messages,
      userDataConsent,
      userData,
      apiEndpoints.message,
      establishmentUuid,
      sessionId,
      texts.error.generic,
      language,
    ]
  )

  const handleRatingSubmit = useCallback(
    async (rating: number, comment?: string) => {
      if (!sessionId) return
      try {
        await submitChatRating(apiEndpoints.rating, {
          session_id: sessionId,
          rating,
          comment,
        })
        setRatingSubmitted(true)
      } catch (error) {
        Sentry.captureException(error)
        const errorMessage: ChatMessage = {
          id: generateId(),
          type: 'error',
          text: texts.error.generic,
        }
        setMessages((prev) => [...prev, errorMessage])
      }
    },
    [apiEndpoints.rating, sessionId, texts.error.generic]
  )

  const cssVariables = {
    '--chat-primary': (themeColors && themeColors.primary) || '#e91e63',
    '--chat-bot-bg': (themeColors && themeColors.botBackground) || '#f5f5f5',
    '--chat-bot-text': (themeColors && themeColors.botTextColor) || '#333333',
  }

  // Determine if we should show the welcome message
  const showWelcomeMessage =
    userData && userData.firstName && userDataConsent !== 'declined'
  const showWelcomeButton = userDataConsent === 'pending'

  return (
    <div
      className={`chat ${isOpen ? 'chat--open' : ''}`}
      style={cssVariables as React.CSSProperties}
      data-testid="chat-container"
    >
      <ChatBubble
        onClick={handleToggle}
        ariaLabel={texts.accessibility.openChat}
        position={bubblePosition}
      />
      {isOpen && (
        <ChatPanel
          messages={messages}
          isLoading={isLoading}
          onSend={handleSend}
          onClose={handleToggle}
          placeholder={texts.input.placeholder}
          closeButtonLabel={texts.accessibility.closeChat}
          logo={logo}
          conversationEnded={conversationEnded}
          ratingSubmitted={ratingSubmitted}
          onRatingSubmit={handleRatingSubmit}
          ratingTexts={texts.rating}
          welcomeMessage={
            showWelcomeMessage
              ? {
                  label: replaceText(
                    userData.email
                      ? texts.welcome.messageWithEmail
                      : texts.welcome.message,
                    {
                      firstName: userData.firstName,
                      email: userData.email || '',
                    }
                  ),
                  notMeButtonLabel: texts.welcome.notMeButtonLabel,
                  showButton: showWelcomeButton,
                  onNotMe: handleNotMe,
                }
              : undefined
          }
        />
      )}
    </div>
  )
}
